import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { v4 as uuidv4 } from 'uuid';

// Use the existing ConditionalRule interface from the main component
interface Condition {
  questionId: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'in' | 'not_in';
  value: any;
  logicalOperator?: 'AND' | 'OR';
}

export interface ConditionalRule {
  id: string;
  type: 'show' | 'hide' | 'require' | 'skip';
  targetQuestionId?: string;
  targetSectionId?: string;
  conditions: Condition[];
}

interface ConditionalRulesEditorProps {
  rules: ConditionalRule[];
  onChange: (rules: ConditionalRule[]) => void;
  availableQuestions: Array<{ id: string; title: string; type: string; sectionId: string; sectionTitle: string }>;
}

export const ConditionalRulesEditor: React.FC<ConditionalRulesEditorProps> = ({
  rules,
  onChange,
  availableQuestions
}) => {
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(
    rules.length > 0 ? rules[0].id : null
  );

  // Add a new rule
  const addRule = () => {
    const newRule: ConditionalRule = {
      id: uuidv4(),
      type: 'show',
      conditions: [{
        questionId: '',
        operator: 'equals',
        value: ''
      }]
    };
    onChange([...rules, newRule]);
    setExpandedRuleId(newRule.id);
  };

  // Update a rule
  const updateRule = (index: number, updates: Partial<ConditionalRule>) => {
    const updatedRules = [...rules];
    updatedRules[index] = { ...updatedRules[index], ...updates };
    onChange(updatedRules);
  };

  // Delete a rule
  const deleteRule = (index: number) => {
    const updatedRules = [...rules];
    updatedRules.splice(index, 1);
    onChange(updatedRules);
    if (rules.length > 0 && index < rules.length - 1) {
      setExpandedRuleId(rules[0].id);
    } else {
      setExpandedRuleId(null);
    }
  };

  // Add a condition to a rule
  const addCondition = (ruleIndex: number) => {
    const updatedRules = [...rules];
    updatedRules[ruleIndex].conditions.push({
      questionId: '',
      operator: 'equals',
      value: ''
    });
    onChange(updatedRules);
  };

  // Update a condition
  const updateCondition = (ruleIndex: number, conditionIndex: number, updates: Partial<Condition>) => {
    const updatedRules = [...rules];
    updatedRules[ruleIndex].conditions[conditionIndex] = {
      ...updatedRules[ruleIndex].conditions[conditionIndex],
      ...updates
    };
    onChange(updatedRules);
  };

  // Delete a condition
  const deleteCondition = (ruleIndex: number, conditionIndex: number) => {
    const updatedRules = [...rules];
    updatedRules[ruleIndex].conditions.splice(conditionIndex, 1);
    onChange(updatedRules);
  };

  // Get the appropriate value input based on question type
  const getValueInput = (question: any, value: any, onChange: (value: any) => void) => {
    if (!question) return <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Value" />;

    switch (question.type) {
      case 'checkbox':
      case 'radio':
      case 'select':
        // For questions with options, show a select dropdown
        return (
          <select
            className="w-full border border-gray-300 rounded-md p-2"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select a value</option>
            {question.options?.map((option: any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      default:
        return <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Value" />;
    }
  };

  // Get the appropriate operators based on question type
  const getOperators = (questionType: string) => {
    switch (questionType) {
      case 'checkbox':
        return [
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Does not contain' }
        ];
      case 'radio':
      case 'select':
      case 'text':
      case 'email':
      case 'url':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Does not equal' }
        ];
      default:
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Does not equal' }
        ];
    }
  };

  // Find a question by ID
  const findQuestion = (questionId: string) => {
    return availableQuestions.find(q => q.id === questionId);
  };

  return (
    <div className="space-y-4 p-4 border border-gray-200 rounded-md">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Conditional Display Rules</h3>
        <Button onClick={addRule} variant="outline" size="sm">
          Add Rule
        </Button>
      </div>

      {rules.length === 0 ? (
        <div className="text-gray-500 text-sm p-4 bg-gray-50 rounded-md">
          No conditional rules. This section will always be displayed.
        </div>
      ) : (
        <div className="space-y-4">
          {rules.map((rule, ruleIndex) => (
            <div key={rule.id} className="border border-gray-200 rounded-md overflow-hidden">
              <div 
                className="flex justify-between items-center p-3 bg-gray-50 cursor-pointer"
                onClick={() => setExpandedRuleId(expandedRuleId === rule.id ? null : rule.id)}
              >
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{rule.type === 'show' ? 'Show' : 'Hide'} this section when:</span>
                  <span className="text-sm text-gray-500">
                    {rule.conditions.length} condition{rule.conditions.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex space-x-2">
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRule(ruleIndex);
                    }}
                    variant="ghost" 
                    size="sm"
                  >
                    Delete
                  </Button>
                  <span className="text-gray-500">
                    {expandedRuleId === rule.id ? '▼' : '►'}
                  </span>
                </div>
              </div>

              {expandedRuleId === rule.id && (
                <div className="p-4 space-y-4">
                  <div className="flex space-x-2">
                    <label className="block text-sm font-medium text-gray-700">Rule Type:</label>
                    <select
                      className="border border-gray-300 rounded-md p-1"
                      value={rule.type}
                      onChange={(e) => updateRule(ruleIndex, { type: e.target.value as any })}
                    >
                      <option value="show">Show section</option>
                      <option value="hide">Hide section</option>
                    </select>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-medium">Conditions:</h4>
                      <Button 
                        onClick={() => addCondition(ruleIndex)} 
                        variant="outline" 
                        size="sm"
                      >
                        Add Condition
                      </Button>
                    </div>

                    {rule.conditions.map((condition, conditionIndex) => {
                      const selectedQuestion = findQuestion(condition.questionId);
                      const operators = selectedQuestion ? getOperators(selectedQuestion.type) : [];

                      return (
                        <div key={conditionIndex} className="p-3 border border-gray-200 rounded-md space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Question:</label>
                              <select
                                className="w-full border border-gray-300 rounded-md p-2"
                                value={condition.questionId}
                                onChange={(e) => updateCondition(ruleIndex, conditionIndex, { questionId: e.target.value })}
                              >
                                <option value="">Select a question</option>
                                {availableQuestions.map(q => (
                                  <option key={q.id} value={q.id}>
                                    {q.sectionTitle} - {q.title}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Operator:</label>
                              <select
                                className="w-full border border-gray-300 rounded-md p-2"
                                value={condition.operator}
                                onChange={(e) => updateCondition(ruleIndex, conditionIndex, { operator: e.target.value as any })}
                                disabled={!selectedQuestion}
                              >
                                <option value="">Select an operator</option>
                                {operators.map(op => (
                                  <option key={op.value} value={op.value}>
                                    {op.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Value:</label>
                              {getValueInput(
                                selectedQuestion,
                                condition.value,
                                (value) => updateCondition(ruleIndex, conditionIndex, { value })
                              )}
                            </div>
                          </div>

                          <div className="flex justify-end">
                            <Button
                              onClick={() => deleteCondition(ruleIndex, conditionIndex)}
                              variant="ghost"
                              size="sm"
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      );
                    })}

                    {rule.conditions.length === 0 && (
                      <div className="text-gray-500 text-sm p-4 bg-gray-50 rounded-md">
                        No conditions. Add at least one condition.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};